import { useCallback, useEffect, useState } from "react";
import { showNotification } from "../../utils/notification";
import { t } from "i18next";
import Loader from "../global/Loader";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { Trans } from "react-i18next";
import useClientProfessionApi from "../../app/useClientProfessionApi";
import NormTable from "./NormTable";
import NormEdit from "./NormEdit";
import useDebounce from "../../utils/useDebounce";
import FilterStringInput from "../global/FilterStringInput";

function NormTab() {
    const clientRiskApi = useClientProfessionApi();

    const [norms, setNorms] = useState(null);
    const [reloadRequest, setReloadRequest] = useState(false);

    const [filterStr, setFilterStr, debouncedFilterStr] = useDebounce("", 500);
    const [loading, setLoading] = useState(false);

    const [showClientOnly, setShowClientOnly] = useState(true);
    const [addFormOpen, setAddFormOpen] = useState(false);

    const onSave = useCallback(async (norm) => {
        if (norm.id) {
            try {
                const updatedNorm = await clientRiskApi.updateNorm(norm);

                const newNorms = norms.map((n) => {
                    if (n.id === norm.id) {
                        return updatedNorm;
                    }
                    return n;
                });
                setNorms(newNorms);
                showNotification(t("normTableUpdateSuccess"), 'success');
            } catch (error) {
                console.error(error);
                showNotification(t("normTableUpdateError") + ": " + error.message, 'danger');
            }
        } else {
            try {
                const n = await clientRiskApi.addNorm(norm);

                setNorms([...norms, n]);
                showNotification(t("normTableCreateSuccess"), 'success');
            } catch (error) {
                console.error(error);
                showNotification(t("normTableCreateError") + ": " + error.message, 'danger');
            }
        }
    }, [clientRiskApi, norms]);

    const onDelete = useCallback(async (id) => {
        try {
            await clientRiskApi.deleteNorm({ id });

            const newNorms = norms.filter((n) => n.id !== id);
            setNorms(newNorms);
            showNotification(t("normTableDeleteSuccess"), 'success');
        } catch (error) {
            console.error(error);
            showNotification(t("normTableDeleteError") + ": " + error.message, 'danger');
        }
    }, [clientRiskApi, norms]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            try {
                const data = await clientRiskApi.getNorms(showClientOnly, debouncedFilterStr);
                setNorms(data);
            } catch (error) {
                console.error(error);
                showNotification(t("normTableFetchError") + ": " + error.message, 'danger');
            }

            setLoading(false);
        }

        const delayedFetch = setTimeout(fetchData, 200);
        return () => clearTimeout(delayedFetch);
    }, [clientRiskApi, debouncedFilterStr, reloadRequest, showClientOnly]);

    return (
        norms === null ? (
            <Loader />
        ) : (
            <div className="mt-4">
                {/* Buttons toolbar */}
                <div className="mt-3 d-flex justify-content-between align-items-center gap-2">
                    <Button
                        variant="primary"
                        onClick={() => setReloadRequest((prev) => !prev)}>
                        <FontAwesomeIcon icon={faRefresh} /> <Trans>normTableReload</Trans>
                    </Button>

                    <div className="flex-grow-1">
                        <FilterStringInput filterStr={filterStr} setFilterStr={setFilterStr} />
                    </div>

                    <Form.Check
                        type="switch"
                        id="showClientOnly"
                        label={t("normTableShowClientOnly")}
                        checked={showClientOnly}
                        onChange={(e) => setShowClientOnly(e.target.checked)}
                    />
                </div>

                {/* Main table */}
                {!loading ? (
                    <NormTable norms={norms} showClientOnly={showClientOnly} onSave={onSave} onDelete={onDelete} />
                ) : (
                    <Loader />
                )}

                {/* Add form */}
                <div className="d-flex justify-content-center mt-3">
                    <Button
                        variant="primary"
                        onClick={() => setAddFormOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} /> <Trans>normTableAdd</Trans>
                    </Button>
                </div>

                {addFormOpen && (
                    <NormEdit
                        norm={{}}
                        onSave={onSave}
                        onCancel={() => setAddFormOpen(false)}
                    />
                )}
            </div>
        )
    );
}

export default NormTab;
